import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/brand/Logo";
import useSeo from "@/lib/useSeo";

export default function AdminLogin() {
  useSeo({ title: "Admin Login", description: "Internal admin sign-in for IslandHop Technologies." });
  const { user, login } = useAdminAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
    } catch (ex) {
      const msg = formatApiError(ex);
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ih-ink text-white relative overflow-hidden grid place-items-center px-5">
      <div className="absolute inset-0 ih-grid-bg opacity-40 [mask-image:radial-gradient(60%_60%_at_50%_50%,#000,transparent_80%)]" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] ih-radial-blue" />

      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3">
          <Logo />
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/40">Admin Console</p>
        </div>
        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-ih-surface border border-ih-line2 rounded-3xl p-8 space-y-5 ih-shadow-blue"
        >
          <div>
            <h1 className="font-display font-extrabold text-2xl text-white">Sign in</h1>
            <p className="text-white/55 text-sm mt-1">Access submissions, applications and waitlist.</p>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-[0.16em] text-white/50">Email</span>
            <div className="flex items-center gap-2 px-3 rounded-xl border border-ih-line2 bg-black/40 focus-within:border-ih-electric transition">
              <EnvelopeSimple size={18} className="text-white/40" />
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="admin-login-email"
                className="flex-1 bg-transparent py-3 text-sm outline-none text-white placeholder:text-white/30"
                placeholder="admin@islandhoptt.com"
                autoComplete="username"
              />
            </div>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs uppercase tracking-[0.16em] text-white/50">Password</span>
            <div className="flex items-center gap-2 px-3 rounded-xl border border-ih-line2 bg-black/40 focus-within:border-ih-electric transition">
              <Lock size={18} className="text-white/40" />
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="admin-login-password"
                className="flex-1 bg-transparent py-3 text-sm outline-none text-white placeholder:text-white/30"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </label>

          {err && (
            <div data-testid="admin-login-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-ih-electric text-white font-semibold hover:scale-[1.01] disabled:opacity-60 transition"
          >
            {loading ? "Signing in..." : "Sign in"} <ArrowRight weight="bold" size={16} />
          </button>

          <p className="text-xs text-white/40 text-center pt-2">
            Restricted access. All login attempts are logged.
          </p>
        </form>
      </div>
    </div>
  );
}
